import type { DayHours, Location } from "@site-factory/config";

export type LocationStatus = {
  label: string;
  timeLabel?: string;
  isOpen: boolean;
};

export function formatDayHours(hours: DayHours): string {
  if (hours.closed) return "Closed";
  if (hours.open && hours.close) return `${hours.open} - ${hours.close}`;
  return "Varies";
}

function parseTimeToMinutes(value?: string) {
  if (!value) return null;
  const trimmed = value.trim();
  const hhmm = trimmed.match(/^(\d{1,2}):(\d{2})$/);
  if (hhmm) {
    const h = parseInt(hhmm[1], 10);
    const m = parseInt(hhmm[2], 10);
    return h * 60 + m;
  }
  const ampm = trimmed.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (ampm) {
    let h = parseInt(ampm[1], 10);
    const m = parseInt(ampm[2], 10);
    const isPM = ampm[3].toUpperCase() === "PM";
    if (h === 12) h = 0;
    return h * 60 + m + (isPM ? 12 * 60 : 0);
  }
  return null;
}

export function getTodayStatus(location: Location, now = new Date()): LocationStatus {
  if (!location.hours || location.hours.length === 0) {
    return { label: "Hours vary", isOpen: false };
  }
  const dayName = new Intl.DateTimeFormat("en-US", { weekday: "long" }).format(now) as DayHours["day"];
  const hours = location.hours.find((entry) => entry.day === dayName);
  if (!hours || hours.closed) {
    return { label: "Closed today", isOpen: false };
  }
  if (!hours.open || !hours.close) {
    return { label: "Hours vary today", isOpen: false };
  }
  const openMinutes = parseTimeToMinutes(hours.open);
  const closeMinutes = parseTimeToMinutes(hours.close);
  const nowMinutes = now.getHours() * 60 + now.getMinutes();
  if (openMinutes !== null && closeMinutes !== null) {
    if (nowMinutes >= openMinutes && nowMinutes < closeMinutes) {
      return { label: `Open now until ${hours.close}`, timeLabel: `${hours.open} - ${hours.close}`, isOpen: true };
    }
    if (nowMinutes < openMinutes) {
      return { label: `Opens at ${hours.open}`, timeLabel: `${hours.open} - ${hours.close}`, isOpen: false };
    }
    return { label: "Closed for the day", timeLabel: `${hours.open} - ${hours.close}`, isOpen: false };
  }
  return { label: "Hours vary today", isOpen: false };
}

export function getLocationPhone(location: Location): string | undefined {
  return location.phone || location.contact?.phone;
}
